import { useState } from 'react';
import { api, type AuditRow } from '../api';
import { useLoad } from '../lib';

// The staff audit log — every invite, join, role change and deactivation, newest first (the BFF's
// append-only staff_audit, the last 100). Read-only: nothing here can be edited or removed.
const ACTIONS: Record<string, string> = {
  'staff.invite': 'Invited',
  'staff.join': 'Joined',
  'staff.role_change': 'Role changed',
  'staff.deactivate': 'Deactivated',
  'staff.reactivate': 'Reactivated',
};

// created_at is a JSON timestamp (UTC); show it in the reader's own time.
const when = (iso: string) => new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });

// meta → a readable line: a role change's from/to, an invite's role, else the raw JSON.
function detail(a: AuditRow): string {
  const m = (a.meta ?? {}) as { role?: string; from?: { role?: string; active?: boolean }; to?: { role?: string; active?: boolean } };
  if (m.from && m.to) {
    const parts = [];
    if (m.from.role !== m.to.role) parts.push(`${m.from.role} → ${m.to.role}`);
    if (m.from.active !== m.to.active) parts.push(m.to.active ? 'reactivated' : 'deactivated');
    return parts.join(', ') || 'no change';
  }
  if (m.role) return `role ${m.role}`;
  return a.meta == null ? '' : JSON.stringify(a.meta);
}

export function AuditPanel() {
  const [action, setAction] = useState('');
  const { data, err } = useLoad(() => api.audit());
  // A role change or deactivation targets a staff id; show who that is (an invite's target is its email).
  const staff = useLoad(() => api.staff().catch(() => []));
  const who = (target: string | null) => {
    const s = (staff.data ?? []).find((x) => x.id === target);
    return s ? s.displayName || s.email || target : target;
  };
  const actions = [...new Set((data ?? []).map((a) => a.action))];
  const rows = (data ?? []).filter((a) => !action || a.action === action);

  return (
    <div className="stack">
      <h1>Audit log</h1>
      <p className="sub">Every staff invite, join, role change and deactivation — the newest 100</p>
      {actions.length > 1 && (
        <div className="toolbar">
          <button className={'chip' + (action === '' ? ' on' : '')} onClick={() => setAction('')}>All</button>
          {actions.map((a) => (
            <button key={a} className={'chip' + (action === a ? ' on' : '')} onClick={() => setAction(a)}>{ACTIONS[a] ?? a}</button>
          ))}
        </div>
      )}
      {err && <p className="msg bad">⚠ {err}</p>}
      <section className="card">
        <table>
          <thead><tr><th>When</th><th>Who</th><th>Action</th><th>Target</th><th>Detail</th></tr></thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.id}>
                <td className="mono muted">{when(a.created_at)}</td>
                <td>{a.actor ?? <span className="muted">—</span>}{a.actor_email && <div className="mono">{a.actor_email}</div>}</td>
                <td><span className="pill type">{ACTIONS[a.action] ?? a.action}</span></td>
                <td>{who(a.target) ?? '—'}</td>
                <td className="small">{detail(a)}</td>
              </tr>
            ))}
            {data && rows.length === 0 && <tr><td colSpan={5} className="loading">No staff actions recorded yet.</td></tr>}
          </tbody>
        </table>
        {!data && !err && <p className="loading">Loading…</p>}
      </section>
    </div>
  );
}
